/**
 * /solo/test-out — Mastery Test-Out Protocol.
 *
 *  Cumulative assessment that gates advancement beyond the 1-week work-ahead cap.
 *  • Must score ≥ 85% to unlock the next 1-week block.
 *  • Max 2 attempts per milestone.
 *  • Failure on attempt 2 → hard-lock, Coach B "Active Rest" recovery screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Zap, Check, X, Trophy, AlertTriangle, ShieldCheck, Play,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const TestOut = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/academy/solo/test-out/status', { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load test-out status'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startTestOut = async () => {
    setBusy(true);
    try {
      const r = await api.post('/academy/solo/test-out/start', {}, { headers: authHeaders() });
      setSession(r.data);
      setAnswers({});
      setResult(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start test-out'); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/academy/solo/test-out/submit', {
        session_id: session.session_id, answers,
      }, { headers: authHeaders() });
      setResult(r.data);
      // Refresh status
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  // Result screen — take precedence over anything else
  if (result) return <ResultScreen result={result} onBack={() => navigate('/solo/athlete')} />;

  // Hard-locked recovery screen
  if (status?.hard_locked) return <RecoveryScreen milestone={status.milestone} onBack={() => navigate('/solo/athlete')} />;

  // Assessment in progress
  if (session) return (
    <Assessment session={session} answers={answers} setAnswers={setAnswers}
      submit={submit} busy={busy} />
  );

  // Intro screen
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-24 pb-24 px-6" data-testid="test-out-intro">
      <div className="container mx-auto max-w-3xl">
        <Link to="/solo/athlete" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3 h-3" /> Athlete Dashboard
        </Link>
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 flex items-center gap-2">
          <Zap className="w-3.5 h-3.5" /> Mastery Test-Out
        </div>
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mt-2">
          Unlock the <span className="text-orange-500">Next Block</span>.
        </h1>
        <p className="text-zinc-300 text-base md:text-lg mt-5 leading-relaxed max-w-2xl">
          You've reached the 1-week work-ahead cap. Coach B's rule: prove mastery, then advance.
        </p>

        <div className="grid md:grid-cols-3 gap-3 mt-8">
          <Stat label="Pass Score" value={`${Math.round((status?.pass_threshold || 0.85) * 100)}%`} />
          <Stat label="Attempts Left" value={`${status?.attempts_remaining || 2}`} sub="max 2" />
          <Stat label="Milestone" value={`#${status?.milestone || 1}`} sub={`${(status?.weeks_earned || 0) * 7} days already earned`} />
        </div>

        <div className="border-2 border-zinc-800 bg-zinc-950 p-6 mt-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">The Rules</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <Rule>Cumulative test spanning the next week's core objectives across all 4 subjects.</Rule>
            <Rule>You need <strong className="text-orange-400">85%+</strong> to pass.</Rule>
            <Rule>You have <strong className="text-orange-400">2 attempts max</strong> for this milestone.</Rule>
            <Rule>Pass → next 7 days of lessons unlock immediately.</Rule>
            <Rule>Fail twice → hard-lock; wait for calendar pacing to catch up.</Rule>
          </ul>
        </div>

        <button onClick={startTestOut} disabled={busy}
          className="mt-6 w-full bg-orange-500 hover:bg-orange-400 text-black py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="test-out-start">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Begin Test-Out · Attempt {(status?.attempts_used || 0) + 1} of 2
        </button>
      </div>
    </div>
  );
};

// ─── Assessment view ───────────────────────────────────────────────────────
const Assessment = ({ session, answers, setAnswers, submit, busy }) => {
  const all = session.questions || [];
  const done = Object.keys(answers).length;
  const canSubmit = done === all.length;
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-24 pb-24 px-6" data-testid="test-out-assessment">
      <div className="container mx-auto max-w-3xl">
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 flex items-center gap-2">
          <Zap className="w-3.5 h-3.5" /> Mastery Test-Out · Milestone #{session.milestone} · Attempt {session.attempt_number} of 2
        </div>
        <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-[1.05] mt-2">
          {done}/{all.length} answered
        </h1>
        <div className="w-full h-1.5 bg-zinc-900 mt-4">
          <div className="h-full bg-orange-500 transition-all" style={{ width: `${(done / Math.max(1, all.length)) * 100}%` }} />
        </div>

        <div className="mt-8 space-y-6">
          {all.map((q, qi) => (
            <div key={qi} className="border-2 border-zinc-900 bg-zinc-950 p-5" data-testid={`to-q-${qi}`}>
              <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500 mb-1">Q{qi + 1} · {q.subject}</div>
              <div className="text-sm font-black text-white mb-4 leading-snug">{q.q}</div>
              <div className="grid grid-cols-1 gap-2">
                {(q.choices || []).map((c, ci) => {
                  const chosen = answers[qi] === ci;
                  return (
                    <button key={ci}
                      onClick={() => setAnswers({ ...answers, [qi]: ci })}
                      className={`text-left px-3 py-2.5 border-2 text-sm transition-all ${
                        chosen ? 'border-orange-500 bg-orange-500/10 text-white' :
                        'border-zinc-800 hover:border-zinc-600 text-zinc-300 bg-black'
                      }`}
                      data-testid={`to-q${qi}-c${ci}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <button onClick={submit} disabled={!canSubmit || busy}
          className="mt-6 w-full bg-orange-500 hover:bg-orange-400 text-black py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="test-out-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Submit Test-Out
        </button>
      </div>
    </div>
  );
};

// ─── Result screen ─────────────────────────────────────────────────────────
const ResultScreen = ({ result, onBack }) => {
  const scorePct = Math.round(result.score * 100);
  if (result.passed) return (
    <div className="min-h-screen bg-gradient-to-b from-orange-500/20 via-[#0B0E14] to-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="to-passed">
      <div className="text-center max-w-lg">
        <Trophy className="w-14 h-14 mx-auto text-orange-500 mb-6" />
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 mb-3">Mastery Confirmed</div>
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02]">{scorePct}%</h1>
        <p className="text-lg text-zinc-300 mt-4">Coach B says: <em className="text-orange-400 not-italic">"That's the tempo."</em></p>
        <p className="text-sm text-zinc-400 mt-4 max-w-md mx-auto">
          Next week of lessons is unlocked. Milestone #{result.milestone} cleared — total earned weeks: +1.
        </p>
        <button onClick={onBack}
          className="mt-8 bg-orange-500 hover:bg-orange-400 text-black px-7 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
          data-testid="to-passed-continue">
          Back to Game Plan
        </button>
      </div>
    </div>
  );

  if (result.hard_locked) return (
    <RecoveryScreen milestone={result.milestone} score={scorePct} missed={result.missed || []} onBack={onBack} />
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-24 pb-24 px-6 flex items-center justify-center" data-testid="to-retry">
      <div className="text-center max-w-lg">
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-zinc-500 mb-3">Not Yet</div>
        <h1 className="text-5xl md:text-6xl font-black uppercase tracking-tight leading-[1.02]">{scorePct}%</h1>
        <p className="text-lg text-zinc-300 mt-4">Below the 85% mastery threshold.</p>
        <p className="text-sm text-zinc-400 mt-4">
          You have <strong className="text-orange-400">{result.attempts_remaining_after ?? 1} attempt{result.attempts_remaining_after === 1 ? '' : 's'}</strong> left. Review the missed lessons, then retry.
        </p>

        {result.missed?.length > 0 && (
          <div className="border-2 border-zinc-900 bg-zinc-950 p-5 mt-6 text-left">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">Missed Skills</div>
            <ul className="space-y-2">
              {result.missed.map((m, i) => (
                <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                  <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-white font-black">{m.lesson_title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.subject}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button onClick={onBack}
          className="mt-8 bg-orange-500 hover:bg-orange-400 text-black px-7 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
          data-testid="to-retry-back">
          Back to Game Plan
        </button>
      </div>
    </div>
  );
};

const RecoveryScreen = ({ milestone, score, missed, onBack }) => (
  <div className="min-h-screen bg-gradient-to-b from-red-500/10 via-[#0B0E14] to-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="to-recovery">
    <div className="text-center max-w-lg">
      <AlertTriangle className="w-14 h-14 mx-auto text-orange-500 mb-6" />
      <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 mb-3">Coach B · Active Rest</div>
      <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight leading-[1.05]">
        Recover Before You <span className="text-orange-500">Advance</span>.
      </h1>
      {score != null && (
        <div className="text-6xl font-black text-white mt-6">{score}%</div>
      )}
      <p className="text-sm text-zinc-300 mt-4 max-w-md mx-auto leading-relaxed">
        Two attempts used on Milestone #{milestone}. Your athlete's next lessons are hard-locked until the standard 180-day calendar catches up. Coach B says: <em className="text-orange-400 not-italic">"The best athletes know when to rest and rewatch film."</em>
      </p>
      {missed && missed.length > 0 && (
        <div className="border-2 border-orange-500/40 bg-zinc-950 p-5 mt-6 text-left">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-3">Focus Areas Logged for Head Coach</div>
          <ul className="space-y-2">
            {missed.map((m, i) => (
              <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                <div>
                  <div className="text-white font-black">{m.lesson_title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.subject}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button onClick={onBack}
        className="mt-8 bg-orange-500 hover:bg-orange-400 text-black px-7 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
        data-testid="to-recovery-back">
        Return to the Locker
      </button>
    </div>
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div className="border-2 border-zinc-900 bg-zinc-950 p-4 text-center">
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">{label}</div>
    <div className="text-3xl font-black text-orange-500 mt-1.5">{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const Rule = ({ children }) => (
  <li className="flex items-start gap-2">
    <Check className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-1" />
    <span>{children}</span>
  </li>
);

export default TestOut;
